import { combineReducers } from 'redux'
import reduceReducers from 'reduce-reducers'
import {
  INCREMENT_TAB_INDEX,
  DECREMENT_TAB_INDEX,
  UPDATE_TAB_INDEX,
  TOGGLE_COLLAPSE,
  UPDATE_REDIRECT,
  UPDATE_INPUT_VALUE,
  TOGGLE_COLLAPSE_CONTENT,
  UPDATE_PARTNER_SITE_SOURCE,
  UPDATE_PARTNER_SITE_FILTER,
  UPDATE_PRODUCT_HIERARCHY_SOURCE,
  UPDATE_PRODUCT_HIERARCHY_FILTER,
  UPDATE_PRODUCT_CLASS_SOURCE,
  UPDATE_PRODUCT_CLASS_FILTER,
  RESET_PARTNER_SITE_FILTER,
  RESET_PRODUCT_HIERARCHY_FILTER,
  CLEAR_PROJECT_CREATE,
  RESET_PRODUCT_CLASS_FILTER,
  PartnerSiteEnum,
  ProductHierarchyEnum,
  ProductClassEnum,
  ENABLE_PRODUCT_CLASS,
  DISABLE_PRODUCT_CLASS,
} from './constants'
import {
  createReducer,
  createNamedReducer,
} from 'helpers/ReducerUtil'
import P from 'helpers/predicates'

const incrementTabIndex = (index) => index + 1
const decrementTabIndex = (index) => {
  if(index > 0) index = index - 1
  return index
}
const partnerSiteIncrementTabIndex = (index) => {
  if (index !== PartnerSiteEnum.PARTNER_SITE.value)
    index = incrementTabIndex(index)
  return index
}
const productHierarchyIncrementTabIndex = (index) => {
  if (index !== ProductHierarchyEnum.BRAND.value)
    index = incrementTabIndex(index)
  return index
}
const updateRedirect = (prev, { redirect }) => P.isNotNil(redirect)
  ? redirect
  : prev
const clearRedirect = () => false
const updateTabIndex = (index, action) => action.index
const updateCollapse = (isCollapse, action) => action.isCollapse
const toggleCollapse = (isCollapse) => !isCollapse
const updateInputValue = (input, action) => action.value
const resetPartnerSiteUiState = () => ({
  tabIndex: 0,
  isCollapse: false,
  showContent: true,
  partnerValue: '',
  partnerSiteValue: '',
  odmValue: '',
})
const resetProductHierarchyUiState = () => ({
  tabIndex: 0,
  isCollapse: false,
  showContent: true,
  [ProductHierarchyEnum.SEGMENT.label]: '',
  [ProductHierarchyEnum.LINE.label]: '',
  [ProductHierarchyEnum.FAMILY.label]: '',
  [ProductHierarchyEnum.PLATFORM.label]: '',
  [ProductHierarchyEnum.SKU.label]: '',
  [ProductHierarchyEnum.BRAND.label]: '',
})

const productHierarchyTabIndexSliceReducer = createReducer(0, {
  [UPDATE_TAB_INDEX]: updateTabIndex,
  [INCREMENT_TAB_INDEX]: productHierarchyIncrementTabIndex,
  [DECREMENT_TAB_INDEX]: decrementTabIndex,
})

const partnerSiteTabIndexSliceReducer = createReducer(0, {
  [UPDATE_TAB_INDEX]: updateTabIndex,
  [INCREMENT_TAB_INDEX]: partnerSiteIncrementTabIndex,
  [DECREMENT_TAB_INDEX]: decrementTabIndex,
})

const collapseSliceReducer = createReducer(false, {
  [TOGGLE_COLLAPSE]: toggleCollapse,
})

const contentCollapseSliceReducer = createReducer(true, {
  [TOGGLE_COLLAPSE_CONTENT]: toggleCollapse,
})

const updateInputValueSliceReducer = createReducer('', {
  [UPDATE_INPUT_VALUE]: updateInputValue,
})

const clearPartnerSiteUiStateReducer = createReducer({}, {
  [RESET_PARTNER_SITE_FILTER]: resetPartnerSiteUiState,
  [CLEAR_PROJECT_CREATE]: resetPartnerSiteUiState,
})

const partnerSiteUiStateReducer = (() => {
  const createReducerWithName = createNamedReducer('PartnerSite')
  return reduceReducers(
    combineReducers({
      tabIndex: createReducerWithName(partnerSiteTabIndexSliceReducer),
      isCollapse: createReducerWithName(collapseSliceReducer),
      showContent: createReducerWithName(contentCollapseSliceReducer),
      partnerValue: createNamedReducer(PartnerSiteEnum.PARTNER.label, updateInputValueSliceReducer),
      partnerSiteValue: createNamedReducer(PartnerSiteEnum.PARTNER_SITE.label, updateInputValueSliceReducer),
      odmValue: createNamedReducer(PartnerSiteEnum.ODM.label, updateInputValueSliceReducer),
    }),
    clearPartnerSiteUiStateReducer,
  )
})()

const clearProductHierarchyUiStateReducer = createReducer({}, {
  [RESET_PRODUCT_HIERARCHY_FILTER]: resetProductHierarchyUiState,
  [CLEAR_PROJECT_CREATE]: resetProductHierarchyUiState,
})

const productHierarchyUiStateReducer = (() => {
  const createReducerWithName = createNamedReducer('ProductHierarchy')
  const reducers = Object.keys(ProductHierarchyEnum)
    .reduce((result, key) => {
      const name = ProductHierarchyEnum[key].label
      return {
        ...result,
        [name]: createNamedReducer(name, updateInputValueSliceReducer)
      }
    }, {})

  return reduceReducers(
    combineReducers({
      tabIndex: createReducerWithName(productHierarchyTabIndexSliceReducer),
      isCollapse: createReducerWithName(collapseSliceReducer),
      showContent: createReducerWithName(contentCollapseSliceReducer),
      ...reducers,
    }),
    clearProductHierarchyUiStateReducer,
  )
})()

const clearProductClassUiStateReducer = createReducer({}, {
  [RESET_PRODUCT_CLASS_FILTER]: (state) => (
    {
      isCollapse: false,
      showContent: true,
      enabled: state.enabled,
      [ProductClassEnum.PRODUCT_CLASS.label]: '',
    })
})

const enabledProductClassReducer = createReducer(false, {
  [ENABLE_PRODUCT_CLASS]: () => true,
  [DISABLE_PRODUCT_CLASS]: () => false,
})

const productClassUiStateReducer = (() => {
  const createReducerWithName = createNamedReducer('ProductClass')
  return reduceReducers(
    combineReducers({
      isCollapse: createReducerWithName(collapseSliceReducer),
      showContent: createReducerWithName(contentCollapseSliceReducer),
      enabled: enabledProductClassReducer,
      productClassValue: createNamedReducer(ProductClassEnum.PRODUCT_CLASS.label, updateInputValueSliceReducer),
    }),
    clearProductClassUiStateReducer,
  )
})()

const redirectSliceReducer = createReducer(false, {
  [UPDATE_REDIRECT]: updateRedirect,
  [CLEAR_PROJECT_CREATE]: clearRedirect,
})

const updateSource = (prev, { source }) => source
  ? [...source]
  : prev

const clearSource = () => []
const updateFilters = (filter, action) => [...action.filters]
const resetFilters = (filter, action, source) => [...source]
const clearFilters = () => []

const partnerSiteFilterSliceReducer = createReducer([], {
  [UPDATE_PARTNER_SITE_SOURCE]: updateSource,
  [UPDATE_PARTNER_SITE_FILTER]: updateFilters,
  [CLEAR_PROJECT_CREATE]: clearFilters,
})

const partnerSiteSourceSliceReducer = createReducer([], {
  [UPDATE_PARTNER_SITE_SOURCE]: updateSource,
  [CLEAR_PROJECT_CREATE]: clearSource,
})

const partnerSiteResetFilterSliceReducer = createReducer([], {
  [RESET_PARTNER_SITE_FILTER]: resetFilters,
})

const partnerSiteCrossReducer = (state, action) => {
  const { filters, source } = state
  return {
    ...state,
    filters: partnerSiteResetFilterSliceReducer(filters, action, source)
  }
}

const productHierarchyFilterSliceReducer = createReducer([], {
  [UPDATE_PRODUCT_HIERARCHY_FILTER]: updateFilters,
  [UPDATE_PRODUCT_HIERARCHY_SOURCE]: updateSource,
})

const productHierarchySourceSliceReducer = createReducer([], {
  [UPDATE_PRODUCT_HIERARCHY_SOURCE]: updateSource,
})

const productHierarchyResetFilterSliceReducer = createReducer([], {
  [RESET_PRODUCT_HIERARCHY_FILTER]: clearFilters,
  [CLEAR_PROJECT_CREATE]: clearFilters,
})

const segmentResetFilterSliceReducer = createReducer([], {
  [RESET_PRODUCT_HIERARCHY_FILTER]: resetFilters,
  [CLEAR_PROJECT_CREATE]: clearFilters,
})

const productHierarchyCrossReducer = (state, action) => {
  const { filters, source } = state
  return {
    ...state,
    filters: productHierarchyResetFilterSliceReducer(filters, action, source),
    source: productHierarchyResetFilterSliceReducer(filters, action, source)
  }
}

const segmentCrossReducer = (state, action) => {
  const { filters, source } = state
  return {
    ...state,
    filters: segmentResetFilterSliceReducer(filters, action, source)
  }
}

const productClassFilterSliceReducer = createReducer([], {
  [UPDATE_PRODUCT_CLASS_SOURCE]: updateSource,
  [UPDATE_PRODUCT_CLASS_FILTER]: updateFilters,
})

const productClassSourceSliceReducer = createReducer([], {
  [UPDATE_PRODUCT_CLASS_SOURCE]: updateSource,
})

const productClassResetFilterSliceReducer = createReducer([], {
  [RESET_PRODUCT_CLASS_FILTER]: resetFilters,
})

const productClassCrossReducer = (state, action) => {
  const { filters, source } = state
  return {
    ...state,
    filters: productClassResetFilterSliceReducer(filters, action, source)
  }
}

const partnerSiteReducer = reduceReducers(
  combineReducers({
    source: partnerSiteSourceSliceReducer,
    filters: partnerSiteFilterSliceReducer,
  }),
  partnerSiteCrossReducer
)

const productHierarchySliceReducer = combineReducers({
  source: productHierarchySourceSliceReducer,
  filters: productHierarchyFilterSliceReducer,
})

const productHierarchyWithResetReducer = (name) => reduceReducers(
  createNamedReducer(name, productHierarchySliceReducer),
  productHierarchyCrossReducer
)

const productHierarchyReducer = (() => {
  const reducers = Object.keys(ProductHierarchyEnum)
    .reduce((result, key) => {
      const name = ProductHierarchyEnum[key].label
      let reducer = productHierarchyWithResetReducer(name, productHierarchySliceReducer)
      if (name === ProductHierarchyEnum.SEGMENT.label)
        reducer = reduceReducers(
          createNamedReducer(name, productHierarchySliceReducer),
          segmentCrossReducer
        )
      return {
        ...result,
        [name]: reducer
      }
    }, {})
  return combineReducers(reducers)
})()

const productClassReducer = reduceReducers(
  combineReducers({
    source: productClassSourceSliceReducer,
    filters: productClassFilterSliceReducer,
  }),
  productClassCrossReducer
)

export default combineReducers({
  partnerSite: partnerSiteUiStateReducer,
  productHierarchy: productHierarchyUiStateReducer,
  productClass: productClassUiStateReducer,
  redirect: redirectSliceReducer,
})

export const projectCreateReducer = combineReducers({
  partnerSite: partnerSiteReducer,
  productHierarchy: productHierarchyReducer,
  productClass: productClassReducer,
})
